import {
  ComponentType,
  memo,
  MemoExoticComponent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { TOAST_DEFAULT_TIMEOUT, TOAST_ERROR_TIMEOUT } from "settings";
import CloseIcon from "mdi-react/CloseIcon";

import ToastGlobalProvider, { showGlobalToast } from "./global";
import { errorMessageFormatter } from "formatters/errorMessage";
import {
  IconButton,
  Snackbar as MuiSnackbar,
  SnackbarOrigin,
  styled,
} from "@mui/material";

interface IProps {
  opened: boolean;
  message?: string;
  timeout?: number;
  error?: Error;
  onClose: () => void;
}

type ToastComponent = MemoExoticComponent<ComponentType<IProps>> & {
  Global?: typeof ToastGlobalProvider;

  show?(message: string, timeout?: number): Promise<void>;
  error?(error: any): Promise<void>;
};

const ContentWrapper = styled("div")<{ isError: boolean }>(
  ({ theme, isError }) => ({
    background: isError ? theme.palette.error.main : "inherit",
  })
);

const Snackbar = styled(MuiSnackbar)(({ theme }) => ({
  [theme.breakpoints.up("sm")]: {
    top: "24px",
    left: "auto",
    right: "24px",
  },
}));

const Toast: ToastComponent = memo((props: IProps) => {
  const [anchorOrigin] = useState<SnackbarOrigin>({
    vertical: "top",
    horizontal: "right",
  });

  const message = useMemo(() => {
    if (props.message) return props.message;

    return errorMessageFormatter({});
  }, [props.message]);

  const isError = useMemo(() => {
    return !props.error ? false : true;
  }, [props.error]);

  const handleClose = useCallback(
    (event: any, reason?: string) => {
      if (reason === "clickaway") return;
      props.onClose();
    },
    [props]
  );

  return (
    <Snackbar
      anchorOrigin={anchorOrigin}
      open={props.opened}
      autoHideDuration={props.timeout}
      onClose={handleClose}
      ContentProps={{
        sx: {
          background: isError ? "#d32f2f" : "#323232"
        },
      }}
      message={
        <ContentWrapper isError={isError}>
          <span>{message}</span>
        </ContentWrapper>
      }
      action={[
        <IconButton
          key="close"
          color="inherit"
          className="close"
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>,
      ]}
    />
  );
});

Toast.Global = ToastGlobalProvider;

Toast.show = (message: string, timeout?: number) => {
  return showGlobalToast(message, null, timeout || TOAST_DEFAULT_TIMEOUT);
};

Toast.error = (message: any) => {
  return showGlobalToast(message, message, TOAST_ERROR_TIMEOUT);
};

export default Toast;
